import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";
import {
  Users,
  Award,
  TrendingUp,
  Star,
  Activity,
  Target,
} from "lucide-react";
import { GlassPanel, formatCrmCurrency, formatNumber, ThemedLegend, getAxisProps, getGridProps, getAngledAxisProps, getTooltipComponent, IncompleteState } from "./ReportShared";
import { useCrmLocale } from "../../SalonCRM/i18n/CrmLocale";
import { DateRange, filterMonthly } from "../analyticsDateRange";
import type { LiveAnalytics } from "../liveAnalyticsAdapter";

const StaffPerformanceReport: React.FC<{ dateRange: DateRange; isDark: boolean; analytics: LiveAnalytics }> = ({ dateRange, isDark, analytics }) => {
  const { lang, t } = useCrmLocale();
  const r = t.analytics.report;
  const fc = (v: number) => formatCrmCurrency(v, lang);
  const STAFF = analytics.staff;
  const MONTHLY_STAFF = analytics.monthlyStaff;

  const f = useMemo(() => {
    const months = filterMonthly(MONTHLY_STAFF, dateRange);

    const staffAppts: Record<string, number> = {};
    for (const row of months) {
      for (const s of STAFF) {
        if (!staffAppts[s.id]) staffAppts[s.id] = 0;
        staffAppts[s.id] += (row[s.id] as number) || 0;
      }
    }

    const staffWithFiltered = STAFF.map(s => ({
      ...s,
      appointments: staffAppts[s.id] || 0,
      revenue: s.appointments > 0 ? Math.round(s.revenue * ((staffAppts[s.id] || 0) / s.appointments)) : 0,
    }));

    const totalAppts = staffWithFiltered.reduce((sum, s) => sum + s.appointments, 0);
    const totalRevenue = staffWithFiltered.reduce((sum, s) => sum + s.revenue, 0);
    const avgUtilization = STAFF.length > 0
      ? Math.round(STAFF.reduce((sum, s) => sum + s.utilization, 0) / STAFF.length)
      : 0;
    const avgRating = STAFF.length > 0
      ? +(STAFF.reduce((sum, s) => sum + s.rating, 0) / STAFF.length).toFixed(1)
      : 0;

    const monthlyTotalData = months.map((row) => ({
      month: row.month,
      total: STAFF.reduce((sum, s) => sum + ((row[s.id] as number) || 0), 0),
    }));

    return { months, staffWithFiltered, totalAppts, totalRevenue, avgUtilization, avgRating, monthlyTotalData };
  }, [dateRange, STAFF, MONTHLY_STAFF]);

  const rankedStaff = [...f.staffWithFiltered].sort((a, b) => b.revenue - a.revenue);

  const comparisonData = rankedStaff.map((s) => ({
    name: s.name.split(" ")[0],
    appointments: s.appointments,
    revenue: s.revenue,
    color: s.color,
  }));

  const txt = isDark ? "text-white" : "text-[#1A1A1A]";
  const txtMuted = isDark ? "text-gray-500" : "text-gray-500";
  const txtMid = isDark ? "text-gray-400" : "text-gray-500";
  const borderSep = isDark ? "border-white/[0.06]" : "border-black/[0.06]";
  const hoverBg = isDark ? "hover:bg-white/[0.08]" : "hover:bg-black/[0.04]";
  const barBg = isDark ? "bg-white/[0.06]" : "bg-black/[0.06]";

  const TooltipComp = getTooltipComponent(isDark);
  const axisProps = getAxisProps(isDark);
  const gridProps = getGridProps(isDark);
  const angledAxisProps = getAngledAxisProps(isDark);

  if (STAFF.length === 0) {
    return (
      <IncompleteState
        isDark={isDark}
        title={r.noStaffTitle}
        description={r.noStaffDescription}
      />
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* ── KPI Summary ─────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {([
          { icon: Users, label: r.totalAppointments, value: formatNumber(f.totalAppts, lang), gradient: "from-violet-500 to-purple-600" },
          { icon: TrendingUp, label: r.bookedRevenueEstimated, value: fc(f.totalRevenue), gradient: "from-emerald-500 to-teal-600" },
          { icon: Target, label: r.avgUtilization, value: `${f.avgUtilization}%`, gradient: "from-blue-500 to-indigo-600" },
          { icon: Star, label: r.avgRating, value: f.avgRating.toFixed(1), gradient: "from-amber-500 to-orange-600" },
        ] as const).map(({ icon: Icon, label, value, gradient }) => (
          <GlassPanel key={label} variant="chartDark" isDark={isDark} className="p-4 sm:p-5">
            <div className="flex items-start gap-3">
              <div className={`w-10 h-10 rounded-xl bg-gradient-to-br ${gradient} flex items-center justify-center flex-shrink-0`} style={{ boxShadow: "0 0 16px rgba(0,0,0,0.3)" }}>
                <Icon className="w-5 h-5 text-white" />
              </div>
              <div>
                <p className={`text-[11px] ${txtMuted} font-medium`}>{label}</p>
                <p className={`text-lg sm:text-xl font-bold ${txt} tracking-tight`}>{value}</p>
              </div>
            </div>
          </GlassPanel>
        ))}
      </div>

      {/* ── Staff Performance Table ─────────────────────── */}
      <GlassPanel variant="chartDark" isDark={isDark} className="p-0 overflow-hidden">
        <div className={`px-5 py-4 border-b ${borderSep}`}>
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center">
              <Award className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className={`text-sm font-bold ${txt}`}>{r.staffPerformance}</h3>
              <p className={`text-[11px] ${txtMuted}`}>{r.staffPerformanceDescription}</p>
            </div>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full min-w-[820px] text-sm">
            <thead>
              <tr className={`border-b ${borderSep}`}>
                {[r.rank, r.employee, r.role, r.appointments, r.revenue, r.utilization, r.rating, r.trend].map((h) => (
                  <th key={h} className={`px-4 py-3 text-start text-[11px] uppercase tracking-wider ${txtMuted} font-semibold`}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rankedStaff.map((s, i) => (
                <tr key={s.id} className={`border-b ${borderSep} ${hoverBg} transition-colors`}>
                  <td className="px-4 py-3">
                    <span
                      className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-xs font-black text-white"
                      style={{ backgroundColor: s.color, boxShadow: `0 0 12px ${s.color}40` }}
                    >
                      #{i + 1}
                    </span>
                  </td>
                  <td className={`px-4 py-3 font-bold ${txt}`}>{s.name}</td>
                  <td className={`px-4 py-3 ${txtMid}`}>{s.role}</td>
                  <td className={`px-4 py-3 text-end ${txtMid}`}>{formatNumber(s.appointments, lang)}</td>
                  <td className={`px-4 py-3 text-end font-bold ${txt}`}>{fc(s.revenue)}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className={`h-1.5 w-20 rounded-full ${barBg} overflow-hidden`}>
                        <div className="h-full rounded-full" style={{ width: `${s.utilization}%`, backgroundColor: s.color }} />
                      </div>
                      <span className={`text-xs font-semibold ${txtMid}`}>{s.utilization}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <Star className="w-3.5 h-3.5 text-amber-400 fill-amber-400" />
                      <span className={`text-xs font-bold ${txt}`}>{s.rating}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[11px] font-bold rounded-full px-2 py-1 ${s.trend >= 0 ? "bg-emerald-500/15 text-emerald-400" : "bg-rose-500/15 text-rose-400"}`}>
                      {s.trend >= 0 ? "+" : ""}{s.trend}%
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </GlassPanel>

      {/* ── Charts Row ──────────────────────────────────── */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
        {/* Appointments Comparison */}
        <GlassPanel variant="chartDark" isDark={isDark} className="p-0 overflow-hidden">
          <div className={`px-5 py-3.5 sm:px-6 sm:py-4 border-b ${borderSep} flex items-center gap-2.5`}>
            <Activity className="w-4 h-4 text-blue-400" style={{ filter: "drop-shadow(0 0 6px rgba(96,165,250,0.5))" }} />
            <h3 className={`text-[13px] font-bold ${txt}`}>{r.appointmentsByStaff}</h3>
            <span className={`text-[10px] ${txtMuted} ms-1`}>{r.comparativeVolume}</span>
          </div>
          <div className="p-4 sm:p-6">
            <ResponsiveContainer width="100%" height={240}>
              <BarChart data={comparisonData} layout="vertical" margin={{ left: 10 }}>
                <defs>
                  {comparisonData.map((entry, i) => (
                    <linearGradient key={`staffBar-${i}`} id={`staffBar-${i}`} x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0%" stopColor={entry.color} stopOpacity={0.25} />
                      <stop offset="100%" stopColor={entry.color} stopOpacity={0.85} />
                    </linearGradient>
                  ))}
                </defs>
                <CartesianGrid {...gridProps} horizontal={false} />
                <XAxis type="number" {...axisProps} />
                <YAxis type="category" dataKey="name" {...axisProps} style={{ fontSize: "11px", fontWeight: 600 }} width={60} />
                <Tooltip content={<TooltipComp />} />
                <Bar dataKey="appointments" name={r.appointments} radius={[4, 10, 10, 4]} barSize={20}>
                  {comparisonData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={`url(#staffBar-${index})`} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </GlassPanel>

        {/* Monthly Trend */}
        <GlassPanel variant="chartDark" isDark={isDark} className="p-0 overflow-hidden">
          <div className={`px-5 py-3.5 sm:px-6 sm:py-4 border-b ${borderSep} flex items-center justify-between`}>
            <div className="flex items-center gap-2.5">
              <TrendingUp className="w-4 h-4 text-emerald-400" style={{ filter: "drop-shadow(0 0 6px rgba(16,185,129,0.5))" }} />
              <h3 className={`text-[13px] font-bold ${txt}`}>{r.monthlyAppointments}</h3>
            </div>
            <ThemedLegend isDark={isDark} items={[{ label: r.appointments, color: "#10B981" }]} />
          </div>
          <div className="p-4 sm:p-6">
            <ResponsiveContainer width="100%" height={240}>
              <AreaChart data={f.monthlyTotalData}>
                <defs>
                  <linearGradient id="staffTrendGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.40} />
                    <stop offset="50%" stopColor="#10B981" stopOpacity={0.10} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                  <filter id="staffGlow">
                    <feGaussianBlur stdDeviation="3" result="blur" />
                    <feMerge><feMergeNode in="blur" /><feMergeNode in="SourceGraphic" /></feMerge>
                  </filter>
                </defs>
                <CartesianGrid {...gridProps} />
                <XAxis dataKey="month" {...angledAxisProps} height={44} />
                <YAxis {...axisProps} />
                <Tooltip content={<TooltipComp />} />
                <Area
                  type="monotone"
                  dataKey="total"
                  name={r.appointments}
                  stroke="#10B981"
                  strokeWidth={2.5}
                  fill="url(#staffTrendGrad)"
                  dot={{ r: 3.5, fill: "#10B981", stroke: "rgba(16,185,129,0.3)", strokeWidth: 4 }}
                  filter="url(#staffGlow)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </GlassPanel>
      </div>
    </div>
  );
};

export default StaffPerformanceReport;
